import _ from 'lodash';
import moment from 'moment';
import { formatDate } from 'common/utils/dateUtils/date-utils';
import { Request } from 'common/domain/models/request';
import { MATERNITY_CODE, PATERNITY_CODE, BIRTH_OF_CHILDREN_CATALOGATION_CODE } from 'vacations-absences.module/common/domain/constants';

class RequestFormHelper {

  static isVacationRequest(type) {
    return type === Request.TYPES.VACATION;
  }

  static isAbsenceRequest(type) {
    return type === Request.TYPES.ABSENCE;
  }

  static getRequestTypes(vacationUserDataService, type) {
    const vacationTypes = vacationUserDataService.getVacationTypes();
    const absenceTypes = vacationUserDataService.getAbsenceTypes();
    const requestTypes = (this.isVacationRequest(type)) ? vacationTypes : absenceTypes;
    return requestTypes.map(this.mapRequestType);
  }

  static getRequestTypesManager(ManagerDataService) {
    if (!ManagerDataService) return;
    const absenceTypes = ManagerDataService.absenceTypes;
    return absenceTypes.map(this.mapRequestType);
  }

  static getRequestCategoriesByCode(requestTypes, code) {
    let requestCategories = [];

    requestTypes.forEach((option) => {
      const optionValue = option.value;
      const match = optionValue.find((vc) => vc.code === code);
      if (match) {
        requestCategories = optionValue.map(this.mapRequestCategory);
      }
    });
    return requestCategories;
  }

  static findRequestTypeByCode(requestTypes, code) {
    let result;

    requestTypes.forEach((requestType) => {
      requestType.value.forEach((requestCategory) => {
        if (requestCategory.code === code) result = requestType;
      });
    });

    return result;
  }

  static findRequestCategoryByCode(requestCategories, code) {
    return requestCategories.find((vc) => vc.value === code);
  }

  static findSicknessLeaveReasonByCode(sicknessLeaveReasons, code) {
    return sicknessLeaveReasons.find((vc) => vc.value === code);
  }

  static mapRequestType(type) {
    let result = {
      option: type.description,
      value: type.categories,
      catalogation: type.catalogation,
    };
    if (type.hourly !== undefined) {
      result.hourly = type.hourly;
    }

    return result;
  }

  static mapRequestCategory(category) {
    let result = {
      option: category.description,
      value: category.code || category.catalogation,
    };

    if (category.maximumDuration !== undefined) {
      result.maximumDuration = category.maximumDuration;
    }
    if (category.hourly !== undefined) {
      result.hourly = category.hourly;
    }

    return result;
  }

  static sanitizeRelapses(relapses) {
    relapses = relapses.map(this.mapRelapse);
    relapses = _.sortBy(relapses, 'value').reverse();
    return relapses;
  }

  static mapRelapse(relapse) {
    const parts = relapse.startDate.split('-');
    const option = [parts[2], parts[1], parts[0]].join('/');
    return {
      option,
      value: relapse.startDate,
    };
  }

  static periodIsSickness(code) {
    return Request.isSick({ code });
  }

  static isDisplacement(code) {
    return Request.isDisplacement({ code });
  }

  static isHourly(requestCategories, code) {
    const category = this.findRequestCategoryByCode(requestCategories, code);
    return category.hourly;
  }

  static getHourlyMax(requestCategories, code) {
    const category = this.findRequestCategoryByCode(requestCategories, code);
    return category.maximumDuration;
  }

  static formIsInvalid(form) {
    return form.$invalid && form.$dirty;
  }

  static getRequestOnCreate(rawRequest) {
    let request = {
      startDate: formatDate(rawRequest.startDate),
      code: rawRequest.code,
    };

    if (rawRequest.endDate) {
      request.endDate = formatDate(rawRequest.endDate);
    }

    if (rawRequest.collegiateNumber) {
      request.collegiateNumber = rawRequest.collegiateNumber;
    }

    if (rawRequest.relapseDate) {
      request.relapseStartDate = rawRequest.relapseDate;
    }

    if (rawRequest.hours) {
      request.hours = rawRequest.hours;
    }

    if (rawRequest.sicknessLeaveDateKnownReason) {
      request.dischargeReason = rawRequest.sicknessLeaveDateKnownReason;
    }

    if (rawRequest.startDateFifthWeek) {
      request.startDateFifthWeek = formatDate(rawRequest.startDateFifthWeek);
    }

    if (rawRequest.endDateFifthWeek) {
      request.endDateFifthWeek = formatDate(rawRequest.endDateFifthWeek);
    }

    if (rawRequest.birthDateChild) {
      request.birthDateChild = formatDate(rawRequest.birthDateChild);
    }

    return request;
  }

  static getRequestOnUpdate(rawRequest, originalRequest) {
    let request = {
      startDate: formatDate(originalRequest.startDate),
      endDate: formatDate(originalRequest.endDate),
      newStartDate: formatDate(rawRequest.startDate),
      newEndDate: formatDate(rawRequest.endDate),
      code: rawRequest.code,
    };

    if (rawRequest.hours) {
      delete request.newEndDate;
      request.hours = rawRequest.hours;
    }

    return request;
  }

  static getRequestOnUpdateSicknessOnCancel(rawRequest, originalRequest) {
    return {
      startDate: formatDate(originalRequest.startDate),
      endDate: formatDate(rawRequest.endDate),
      code: originalRequest.code,
      dischargeReason: rawRequest.leaveSicknessReason,
      collegiateNumber: rawRequest.collegiateNumber,
    };
  }

  static getLeaveSicknessReasons(UserDataService) {
    const reasonsTypes = UserDataService.getDischargeReasons();
    const mappedReasons = reasonsTypes.map(this.mapRequestCategory);
    return mappedReasons;
  }

  static mapSickLeaveReports(sickLeaveReports) {
    return sickLeaveReports.map((report) => {
      report.date = new Date(report.sickPartDate);
      report.disabled = true;
      return report;
    });
  }

  static getSickLeaveReportsOnCreate(sickLeaveReports, originalPeriod) {
    return sickLeaveReports
      .filter((report) => {
        const reportIsFromDatabase = (report.disabled === true);
        return !reportIsFromDatabase;
      })
      .map((report) => {
        report.date.setHours(0, 0, 0, 0);
        return {
          absenceDate: formatDate(originalPeriod.startDate),
          sickPartDate: formatDate(report.date),
          order: parseInt(report.order),
        };
      });
  }

  static requestTypeSwitchEditable(request, isInception) {
    const requestedVacation = Request.isVacation(request) && Request.isRequested(request);
    const pastRequest = moment().isAfter(request.startDate);
    return (requestedVacation && pastRequest) && !isInception;
  }

  static getMaximumDurationPaternityAndMaternity(requestTypes) {
    let maximumDuration = {
      paternity: 0,
      oldPaternity: 0,
      maternity: 0,
    };
    let resultType = requestTypes.filter(objectFiltered => {
      return (objectFiltered.catalogation === BIRTH_OF_CHILDREN_CATALOGATION_CODE);
    });

    if (resultType.length > 0) {
      let resultPaternity = resultType[0].value.filter(objectFiltered => {
        return (objectFiltered.code === PATERNITY_CODE);
      });

      let resultMaternity = resultType[0].value.filter(objectFiltered => {
        return (objectFiltered.code === MATERNITY_CODE);
      });
      if (resultPaternity.length || resultMaternity.length) {
        maximumDuration.paternity = resultPaternity[0].maximumDuration;
        maximumDuration.maternity = resultMaternity[0].maximumDuration;
      }
    }

    return maximumDuration;
  }
  static getMaximumDurationMATPAT(initDate, reason, periods) {

    const targetDate = moment(initDate);

    const match = periods.find(p => {
      const start = moment(p.startDate, 'YYYY-MM-DD');
      const end = moment(p.endDate, 'YYYY-MM-DD');
      return (
        p.reason === reason &&
        targetDate.isBetween(start, end, undefined, '[]')
      );
    });

    return match ? match.totalDuration : null;
  }
}

export default RequestFormHelper;



// WEBPACK FOOTER //
// ./client/app/vacations-absences.module/components/modals/modalRequest/requestForm/requestFormHelper.js